import { ItemIDs } from '../../Config/Items';
import Core from '../../Core/Core';
import getLogger, { ILogger } from '../../Core/Logger';
import { AdContexts, AdGameAreas, AdRewardTypes } from '../../DeltaTracking/AdParamBuilder';
import { TGameAreas, TransactionActionTypes } from '../../DeltaTracking/ResourceParamBuilder';
import { IFBFriend } from '../../SDK/SDKfb';
import SoundManager from '../../Sound/SoundManager';
import { Actions } from '../../State/Actions/Actions';
import { StuckReasons } from '../../State/reducers/level';
import { MissionState } from '../../State/reducers/missions';
import { IRewardItem, storeRewards } from '../../State/reducers/score';
import {
    hasAlreadyBeenRequestedGiftTo,
    hasAlreadyBeenSentGiftTo,
} from '../../State/reducers/social';
import { Views } from '../../State/reducers/view';
import { COOLDOWNPRIZEWHEEL } from '../../UI/Map/PrizeWheelPopup';
import DateUtils from '../../Utils/DateUtils';
import HudView from '../../View/HudView';
import { Manager } from '../Manager';
import { Popups } from '../Popups';
import ResourceTracking from '../Tracking/ResourceTracking';
import SocialTracking from '../Tracking/SocialTracking';
import { Animations } from './Animations';
import { HudActions, HudEmitter } from './HudEmitter';
import { Ui } from './Ui';
import { XPromoTracking } from '../Tracking/XPromoTracking';

const logger: ILogger = getLogger('[HUD]:');

export class Hud extends Manager {
    public view: HudView;
    public ui: Ui;
    public popups: Popups;
    public animations: Animations;
    private interstitialCounter: number = 0;

    constructor(container: PIXI.Container) {
        super();
        this.view = new HudView();
        container.addChild(this.view);

        this.ui = new Ui(this.view);
        this.popups = new Popups(this.view);
        this.animations = new Animations(this.view);

        HudEmitter.instance.on(
            HudActions.SOCIALHUB_REQUEST_GIFT,
            this.requestGiftHandler.bind(this)
        );
        HudEmitter.instance.on(HudActions.SOCIALHUB_SEND_GIFT, this.sendGiftHandler.bind(this));
        HudEmitter.instance.on(
            HudActions.SOCIALHUB_START_LOAD_SOCIAL,
            this.handleLoadSocialStart.bind(this)
        );
        HudEmitter.instance.on(
            HudActions.SOCIALHUB_STOP_LOAD_SOCIAL,
            this.handleLoadSocialStop.bind(this)
        );

        HudEmitter.instance.on(HudActions.RECEIVE_REWARDS, this.storeRewards.bind(this));
        HudEmitter.instance.on(HudActions.QUIT_GAME, this.handleQuitGame.bind(this));
        HudEmitter.instance.on(HudActions.TOGGLE_MUSIC, this.handleToggleMusic.bind(this));
        HudEmitter.instance.on(HudActions.TOGGLE_SOUND, this.handleToggleSound.bind(this));
        HudEmitter.instance.on(HudActions.ASK_FRIEND, this.handleAskFriend.bind(this));
        HudEmitter.instance.on(HudActions.LEVEL_INIT, this.loadInterstitialAd.bind(this));
        HudEmitter.instance.on(HudActions.SET_PAUSED, this.handlePause, this);
        HudEmitter.instance.on(HudActions.QUIT_GAME, this.showInterstitialAd.bind(this));
        HudEmitter.instance.on(HudActions.LEVEL_RETRY, this.showInterstitialAd.bind(this));
        HudEmitter.instance.on(HudActions.SET_PAUSE_VISIBLE, this.setPauseVisible, this);
        HudEmitter.instance.on(HudActions.SWITCH_GAME, this.switchGame, this);
        this.addWatcher(this.updateLevelTimer, state => state.StateScore.winTime);
        this.popups.shop.consumePurchases().catch(x => logger.error(x));
    }

    public update() {
        this.ui.update();

        if (this.view.socialPopup) {
            this.view.socialPopup.update(
                Core.store.getState().StateSocial.sentGifts,
                Core.store.getState().StateSocial.requestedGifts
            );
        }

        const refreshing = Core.store
            .getState()
            .StateMissions.missions.filter(m => !m || m.missionState === MissionState.REFRESHING);
        if (
            Core.store.getState().StateMissions.refreshTimer !== 0 &&
            refreshing.length > 0 &&
            this.view.journalPopup
        ) {
            this.view.journalPopup.update(Core.store.getState().StateMissions);
        }

        if (this.view.prizeWheelBtn) {
            if (Core.store.getState().StatePlayer.prizeWheelCooldown !== 0) {
                const timeRemaining = DateUtils.timeRemainingTo(
                    COOLDOWNPRIZEWHEEL + Core.store.getState().StatePlayer.prizeWheelCooldown
                );

                if (timeRemaining < 0) {
                    Core.store.dispatch(Actions.setPriceWheelCooldown(0));
                }
                this.view.prizeWheelBtn.updateIcon(timeRemaining < 0);
            }
        }
    }

    public cleanUp() {
        super.cleanUp();
        this.view.destroy();
    }

    public async handleAskFriend(
        friend: IFBFriend,
        item: IRewardItem,
        callback: (accepted: boolean) => void
    ) {
        this.handleLoadSocialStart();
        Core.store.dispatch(Actions.askFriend(friend));
        const status = await Core.sdk.messages.askFriend(friend, item);
        if (status) {
            const itemId = item.id as ItemIDs;

            Core.store.dispatch(Actions.addFriendBooster(itemId));
            if (Core.store.getState().StateBoard.ready) {
                Core.store.dispatch(Actions.activateFriendsBoosters());
            }
        } else {
            Core.store.dispatch(Actions.askFriendComplete());
        }
        this.handleLoadSocialStop();
        if (callback) {
            callback(status);
        }
    }

    public handleToggleMusic() {
        const isOn = Core.store.getState().StateSound.music;
        Core.store.dispatch(Actions.toggleMusic(!isOn));
        SoundManager.setMusicEnabled(!isOn);
    }

    public handleToggleSound() {
        const isOn = Core.store.getState().StateSound.soundFx;
        Core.store.dispatch(Actions.toggleSound(!isOn));
        SoundManager.setSoundEabled(!isOn);
    }

    public handleQuitGame() {
        Core.store.dispatch(Actions.tutorialStart(100)); // clearing tutorial
        const reason = Core.store.getState().StateLevel.timed
            ? StuckReasons.TIME
            : StuckReasons.MATCHES;
        Core.store.dispatch(Actions.setLevelStuck({ reason, stuck: false }));
        Core.store.dispatch(Actions.changeView(Views.MAP));
    }

    private async switchGame(appId: string, gameName: string, placement: string) {
        XPromoTracking.xPromoAccept(gameName);
        this.handleLoadSocialStart();
        await Core.sdk.messages.switchGame(appId, gameName, placement);
        this.handleLoadSocialStop();
    }

    private updateLevelTimer(winTime: number) {
        if (!this.ui || !this.ui.topUi || winTime <= 0) {
            return;
        }
        this.view.topUi.setTime(winTime);
    }

    private handleLoadSocialStart() {
        this.view.createLoadSocial();
    }

    private handleLoadSocialStop() {
        this.view.removeLoadSocial().catch(err => {
            console.log(['error with remove load social stop', err]);
        });
    }

    private async requestGiftHandler(id: string) {
        SocialTracking.clickGiftRequest();
        this.view.createLoadSocial();
        const result = await Core.sdk.messages.requestGift(id);
        this.view.removeLoadSocial().catch(err => {
            console.log(['error with remove load social request', err]);
        });
        if (result) {
            Core.store.dispatch(Actions.requestGift(id));
            const requestGift = hasAlreadyBeenRequestedGiftTo(
                Core.store.getState().StateSocial,
                id
            );
            if (requestGift) {
                Core.store.dispatch(Actions.addGiftToCounter());
            }
        }
    }

    private async sendGiftHandler(id: string) {
        SocialTracking.clickGiftSend();
        this.view.createLoadSocial();
        const result = await Core.sdk.messages.sendGift(id);
        this.view.removeLoadSocial().catch(err => {
            console.log(['error with remove load social send', err]);
        });
        if (result) {
            Core.store.dispatch(Actions.sendGift(id));
            const sentGift = hasAlreadyBeenSentGiftTo(Core.store.getState().StateSocial, id);
            if (sentGift) {
                Core.store.dispatch(Actions.addGiftToCounter());
            }
        }
    }

    /**
     * stores the rewards from the social reward box
     * @param rewards
     */

    private storeRewards(
        rewards: IRewardItem[],
        type: TransactionActionTypes = TransactionActionTypes.WHEEL_SPIN
    ) {
        storeRewards(rewards, type);
        rewards.forEach(r => {
            ResourceTracking.sendRewardResourceEvent(r.id, r.val as number, type, TGameAreas.MAP);
        });
    }

    private async loadInterstitialAd() {
        const { StatePlayer } = Core.store.getState();
        if (
            StatePlayer.lastLevelIndex >= 10 &&
            ++this.interstitialCounter % 2 === 0 &&
            StatePlayer.purchases.length === 0
        ) {
            logger.log('interstitial ad requested');
            const lastDate = new Date(StatePlayer.lastInterstitialDate);
            const currentDate = new Date();
            const firstOfTheDay = lastDate.toDateString() !== currentDate.toDateString();
            const adPlacement = firstOfTheDay ? 'first' : 'second';
            try {
                Core.delta.setAdParams(AdRewardTypes.NONE, AdContexts.NONE, AdGameAreas.LEVEL, -1);
                await Core.sdk.ads.showAd(
                    'i',
                    adPlacement,
                    () => {
                        Core.store.dispatch(Actions.interstitialAdWatched());
                    },
                    () => {
                        logger.error('error showing interstitial ads');
                    },
                    false
                );
            } catch (e) {
                logger.error(['error showing interstitial ads:', e]);
            }
        }
    }

    private async showInterstitialAd() {
        logger.log('show interstitial ad');
        return Core.sdk.ads.showLoadedAd();
    }

    private setPauseVisible(visible: boolean) {
        Core.store.dispatch(Actions.pauseMenuVisible(visible));
    }

    private handlePause(pause: boolean) {
        if (this.ui.topUi) {
            this.ui.topUi.setPaused(pause);
        }
        if (this.view) {
            this.view.setPaused(pause);
        }
    }
}
